// ─── Credit System ────────────────────────────────────────────────────────────
// Manages user credit balance and seasonal reminders in localStorage.
// Replace localStorage calls with API calls when backend is ready.

const CREDIT_KEY = "done_credits";
const REMINDER_KEY = "done_reminders";
const CREDIT_PER_REMINDER = 15;

// ── Credits ───────────────────────────────────────────────────────────────────

export const getCredits = () => {
  try {
    const raw = localStorage.getItem(CREDIT_KEY);
    return raw ? JSON.parse(raw) : { balance: 0, history: [] };
  } catch {
    return { balance: 0, history: [] };
  }
};

export const getCreditBalance = () => getCredits().balance;

const saveCredits = (data) => {
  localStorage.setItem(CREDIT_KEY, JSON.stringify(data));
};

export const addCredits = (amount, reason) => {
  const credits = getCredits();
  credits.balance = +(credits.balance + amount).toFixed(2);
  credits.history = [
    { id: Date.now(), amount, reason, date: new Date().toISOString(), type: "earned" },
    ...credits.history,
  ];
  saveCredits(credits);
  return credits.balance;
};

export const deductCredits = (amount) => {
  const credits = getCredits();
  const deduct = Math.min(amount, credits.balance);
  if (deduct <= 0) return 0;
  credits.balance = +(credits.balance - deduct).toFixed(2);
  credits.history = [
    { id: Date.now(), amount: -deduct, reason: "Applied to booking", date: new Date().toISOString(), type: "spent" },
    ...credits.history,
  ];
  saveCredits(credits);
  return deduct;
};

export const getCreditHistory = () => getCredits().history;

// ── Reminders ─────────────────────────────────────────────────────────────────

export const getReminders = () => {
  try {
    const raw = localStorage.getItem(REMINDER_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

export const getSeasonReminder = (seasonKey) => {
  return getReminders()[seasonKey] || null;
};

// Returns { alreadyCredited, newBalance }
export const setSeasonReminder = (seasonKey, date) => {
  const reminders = getReminders();
  const existing = reminders[seasonKey];
  const alreadyCredited = existing?.credited === true;

  reminders[seasonKey] = { date, credited: alreadyCredited ? true : false };

  if (!alreadyCredited) {
    const season = seasonKey.charAt(0).toUpperCase() + seasonKey.slice(1);
    addCredits(CREDIT_PER_REMINDER, `${season} reminder set`);
    reminders[seasonKey].credited = true;
  }

  localStorage.setItem(REMINDER_KEY, JSON.stringify(reminders));

  return {
    alreadyCredited,
    newBalance: getCreditBalance(),
    credited: !alreadyCredited,
  };
};

export const removeSeasonReminder = (seasonKey) => {
  const reminders = getReminders();
  if (reminders[seasonKey]) {
    // Keep credited flag so credit isn't awarded again on re-set
    reminders[seasonKey] = { date: null, credited: reminders[seasonKey].credited };
    localStorage.setItem(REMINDER_KEY, JSON.stringify(reminders));
  }
};

export const CREDIT_PER_REMINDER_AMOUNT = CREDIT_PER_REMINDER;
